import { useEffect, useRef } from 'react';
import { useAppSelector } from './redux';
import { createAutosave } from '../utils/storage';
import { buildLayoutSaveProject, createLayoutExportBase } from '../utils/projectExport';
import { selectExportableEquipmentInstances } from '../features/equipment/equipmentSelectors';
import { AUTOSAVE_INTERVAL } from '../constants';
import { debounce } from '../utils/geometry';

export const useAutosave = () => {
  const elements = useAppSelector((state) => state.layout.elements);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const trackNodeAnnotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const zoomLevel = useAppSelector((state) => state.ui.zoomLevel);
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const layersState = useAppSelector((state) => state.layers);
  // Only overlays the user can see are persisted; hidden DXF layers must not
  // leak into the autosave payload.
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstances);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const equipmentVisible = useAppSelector((state) => state.equipment.visible);
  const equipmentShowLabels = useAppSelector((state) => state.equipment.showLabels);
  const equipmentShowPorts = useAppSelector((state) => state.equipment.showPorts);
  const layoutSaveMarkedIds = useAppSelector((state) => state.equipment.layoutSaveMarkedIds);
  const floorPlanName = useAppSelector((state) => state.floorplan.name);
  const floorPlanId = useAppSelector((state) => state.floorplan.floorPlanId);

  const lastSavedRef = useRef<string>('');

  useEffect(() => {
    const debouncedSave = debounce(() => {
      const currentState = JSON.stringify({
        elements,
        elementOrder,
        trackNodeAnnotations,
        equipmentInstances,
        equipmentTemplates,
        layoutSaveMarkedIds,
      });

      // Only save if state has changed
      if (currentState !== lastSavedRef.current && Object.keys(elements).length > 0) {
        const layoutBase = createLayoutExportBase({
          elements,
          elementOrder,
          layers: layersState.layers,
          layerOrder: layersState.layerOrder,
          groups: layersState.groups,
          viewport: { x: 0, y: 0, scale: zoomLevel },
          gridSettings,
          trackNodeAnnotations,
          name: 'Autosaved Fab Layout',
        });

        const project = buildLayoutSaveProject({
          layoutBase,
          templates: Object.values(equipmentTemplates),
          instances: equipmentInstances,
          markedIds: layoutSaveMarkedIds,
          floorPlanId,
          floorPlanName,
          equipmentUi: {
            visible: equipmentVisible,
            showLabels: equipmentShowLabels,
            showPorts: equipmentShowPorts,
          },
        });

        createAutosave(project);
        lastSavedRef.current = currentState;
        console.log('Autosaved at', new Date().toLocaleTimeString());
      }
    }, AUTOSAVE_INTERVAL);

    debouncedSave();
  }, [
    elements,
    elementOrder,
    trackNodeAnnotations,
    zoomLevel,
    gridSettings,
    layersState,
    equipmentInstances,
    equipmentTemplates,
    equipmentVisible,
    equipmentShowLabels,
    equipmentShowPorts,
    layoutSaveMarkedIds,
    floorPlanName,
    floorPlanId,
  ]);
};
